// src/components/UserProfile.js
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getUserProfile, getUserLapsByCircuit } from '../utils/firestoreUtils';
import { Container, Grid, Avatar, List, ListItem, Typography } from '@mui/material';
import { Link } from 'react-router-dom';

const formatLapTime = (time) => {
  if (!time) return '';
  const s = parseFloat(time);
  const m = Math.floor(s / 60), sec = Math.floor(s % 60), ms = Math.round((s % 1) * 1000);
  if (m === 0) return `${sec.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
  return `${m}:${sec.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
};

// ── Clean lap card: map on white panel, dark info strip ───────
function ProfileLapCard({ lap, showYear = false }) {
  const lapYear = lap.date
    ? new Date(lap.date.toDate ? lap.date.toDate() : lap.date).getFullYear()
    : null;

  const isCurrentSeason = !showYear;
  const hasPoints = lap.points > 0;

  const circuitId = lap.track ? lap.track.split('-')[0] : null;

  return (
    <Link to={circuitId ? `/circuit/${circuitId}` : '#'} style={{ textDecoration: 'none', display: 'block' }}>
    <div style={{
      borderRadius: 'var(--radius-lg)',
      overflow: 'hidden',
      border: '1px solid var(--border)',
      boxShadow: 'var(--shadow-sm)',
      background: 'var(--text-primary)',
      display: 'flex',
      flexDirection: 'column',
      minHeight: 200,
      transition: 'transform var(--transition), box-shadow var(--transition)',
      cursor: 'pointer',
    }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = 'var(--shadow-lg)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; }}
    >
      {/* Track map — white background so PNG lines show cleanly */}
      <div style={{
        flex: 1,
        background: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        position: 'relative',
        minHeight: 120,
      }}>
        {lap.trackMap ? (
          <img
            src={lap.trackMap}
            alt=""
            style={{ maxWidth: '100%', maxHeight: 110, objectFit: 'contain', display: 'block' }}
          />
        ) : (
          /* No map fallback — subtle track icon */
          <svg width="64" height="48" viewBox="0 0 64 48" fill="none">
            <rect x="8" y="8" width="48" height="32" rx="8" stroke="#d0d5de" strokeWidth="2" fill="none" strokeDasharray="5 3" />
            <text x="32" y="28" textAnchor="middle" fontSize="11" fill="#b0b8c8" fontFamily="sans-serif">No map</text>
          </svg>
        )}

        {/* Lap time badge — top left, over the white map area */}
        <div style={{
          position: 'absolute',
          top: 10,
          left: 10,
          background: 'var(--text-primary)',
          borderRadius: 'var(--radius-md)',
          padding: '5px 10px',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}>
          <span style={{
            fontFamily: 'var(--font-mono)',
            fontSize: '1rem',
            fontWeight: 700,
            color: '#ffffff',
            letterSpacing: '-0.01em',
            lineHeight: 1,
          }}>
            {formatLapTime(lap.bestLap)}
          </span>
        </div>

        {/* Points badge — top right, only for current season with points */}
        {isCurrentSeason && hasPoints && (
          <div style={{
            position: 'absolute',
            top: 10,
            right: 10,
            background: 'rgba(240,165,0,0.15)',
            border: '1px solid rgba(240,165,0,0.35)',
            borderRadius: 'var(--radius-pill)',
            padding: '3px 9px',
            fontSize: '0.72rem',
            fontWeight: 700,
            color: 'var(--gold)',
          }}>
            {lap.points} {lap.points === 1 ? 'pt' : 'pts'}
          </div>
        )}
      </div>

      {/* Info strip — dark, clean */}
      <div style={{
        background: 'var(--text-primary)',
        padding: '10px 14px',
        borderTop: '1px solid rgba(255,255,255,0.08)',
      }}>
        <div style={{
          fontWeight: 700,
          fontSize: '0.85rem',
          color: '#ffffff',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          lineHeight: 1.3,
          marginBottom: 3,
        }}>
          {lap.trackName || 'Unknown'}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {lap.eventName && (
            <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.55)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {lap.eventName}
            </span>
          )}
          {showYear && lapYear && (
            <span style={{
              fontSize: '0.7rem',
              fontFamily: 'var(--font-mono)',
              color: 'rgba(255,255,255,0.4)',
              marginLeft: 'auto',
              flexShrink: 0,
            }}>
              {lapYear}
            </span>
          )}
        </div>
      </div>
    </div>
    </Link>
  );
}

function UserProfile() {
  const { userId } = useParams();
  const [profile,             setProfile]             = useState(null);
  const [selectedVehicleId,   setSelectedVehicleId]   = useState(null);
  const [currentSeasonLaps,   setCurrentSeasonLaps]   = useState([]);
  const [historicalLaps,      setHistoricalLaps]      = useState([]);
  const [currentSeasonPoints, setCurrentSeasonPoints] = useState(0);

  const getSelectedVehicleData = () => {
    if (!profile || !selectedVehicleId) return { images: [], mods: [] };
    return profile.vehicles.find(v => v.id === selectedVehicleId) || { images: [], mods: [] };
  };

  const getFilteredLaps = (lapsArray) => {
    if (!selectedVehicleId) return lapsArray;
    return lapsArray.filter(lap => lap.vehicleId === selectedVehicleId);
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const prof = await getUserProfile(userId);
        setProfile(prof);
        if (prof.vehicles?.length > 0) setSelectedVehicleId(prof.vehicles[0].id);
        const lps = await getUserLapsByCircuit(userId);
        const currentYear = new Date().getFullYear();
        const current = lps.filter(lap => {
          const d = lap.date?.toDate ? lap.date.toDate() : new Date(lap.date);
          return d.getFullYear() === currentYear;
        });
        const hist = lps.filter(lap => {
          const d = lap.date?.toDate ? lap.date.toDate() : new Date(lap.date);
          return d.getFullYear() < currentYear;
        });
        setCurrentSeasonLaps(current);
        setHistoricalLaps(hist);
        setCurrentSeasonPoints(current.reduce((sum, lap) => sum + (lap.points || 0), 0));
      } catch (err) {
        console.error('Error fetching profile:', err);
      }
    };
    fetchProfile();
  }, [userId]);

  if (!profile) return (
    <Container maxWidth="lg">
      <div style={{ padding: '60px 0', textAlign: 'center', color: 'var(--text-tertiary)' }}>Loading…</div>
    </Container>
  );

  const vehicleData = getSelectedVehicleData();
  const modsByCategory = (vehicleData.mods || []).reduce((acc, mod) => {
    if (!acc[mod.category]) acc[mod.category] = [];
    acc[mod.category].push(mod.name || mod.desc || 'Unnamed modification');
    return acc;
  }, {});

  const seasonLaps    = getFilteredLaps(currentSeasonLaps);
  const histLaps      = getFilteredLaps(historicalLaps);

  return (
    <Container maxWidth="lg" sx={{ pb: 6 }}>
      {/* ── Profile header ── */}
      <Grid container spacing={3} alignItems="center" style={{ marginBottom: 32 }}>
        <Grid item xs={12} md={8}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Avatar src={profile.headshot} alt="Headshot" sx={{ width: 72, height: 72 }} />
            <div>
              <h1 className="page-title" style={{ marginBottom: 4 }}>{profile.name}</h1>
              {profile.bio && (
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: 1.6 }}>{profile.bio}</p>
              )}
            </div>
          </div>
        </Grid>
        <Grid item xs={12} md={4} style={{ textAlign: 'right' }}>
          <div className="stat-number">{currentSeasonPoints}</div>
          <div className="stat-label">Season Points</div>
        </Grid>
      </Grid>

      {/* ── Vehicle tabs ── */}
      {profile.vehicles?.length > 1 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-tertiary)', marginBottom: 8 }}>
            Vehicles
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {profile.vehicles.map(v => (
              <button key={v.id} onClick={() => setSelectedVehicleId(v.id)} style={{
                padding: '7px 16px',
                borderRadius: 'var(--radius-pill)',
                border: selectedVehicleId === v.id ? 'none' : '1px solid var(--border-strong)',
                background: selectedVehicleId === v.id ? 'var(--accent)' : 'var(--surface)',
                color: selectedVehicleId === v.id ? '#fff' : 'var(--text-secondary)',
                fontFamily: 'var(--font-body)', fontWeight: 600, fontSize: '0.82rem',
                cursor: 'pointer', transition: 'all var(--transition)',
              }}>
                {v.name || `Vehicle ${v.id.slice(-4)}`}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Vehicle images ── */}
      {vehicleData.images?.length > 0 && (
        <div className="card-grid" style={{ marginBottom: 8 }}>
          {vehicleData.images.map((img, idx) => (
            <img key={idx} src={img} alt="car"
              style={{ width: '100%', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', objectFit: 'cover', maxHeight: 220 }} />
          ))}
        </div>
      )}

      {/* ── Modifications ── */}
      {Object.keys(modsByCategory).length > 0 && (
        <>
          <h2 className="section-heading">Modifications</h2>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', boxShadow: 'var(--shadow-sm)', marginBottom: 8 }}>
            {Object.entries(modsByCategory).map(([category, descs], i, arr) => (
              <div key={category} style={{ padding: '12px 20px', borderBottom: i < arr.length - 1 ? '1px solid var(--border)' : 'none' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-tertiary)', marginBottom: 6 }}>
                  {category}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {descs.map((desc, j) => (
                    <span key={j} style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)', padding: '3px 10px', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                      {desc}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── This Season's Laps ── */}
      {seasonLaps.length > 0 && (
        <>
          <h2 className="section-heading">This Season's Laps</h2>
          <div className="card-grid">
            {seasonLaps.map((lap, idx) => (
              <ProfileLapCard key={idx} lap={lap} showYear={false} />
            ))}
          </div>
        </>
      )}

      {/* ── Historical Laps ── */}
      {histLaps.length > 0 && (
        <>
          <h2 className="section-heading">Historical Laps</h2>
          <div className="card-grid">
            {histLaps.map((lap, idx) => (
              <ProfileLapCard key={idx} lap={lap} showYear={true} />
            ))}
          </div>
        </>
      )}
    </Container>
  );
}

export default UserProfile;
